export type AuthContext = 'sign-in' | 'sign-up' | 'change-password' | 'reset';

export interface AuthErrorLike {
  status?: number;
  code?: string;
  message?: string;
  /** Seconds from a 429's Retry-After header, when the caller read it. */
  retryAfter?: number;
}

/** Same limits as the Worker (`apps/worker/src/auth/password-rules.ts`). */
export const MIN_PASSWORD_LENGTH = 10;
export const MAX_PASSWORD_LENGTH = 128;

/** User-facing copy (brand.md: calm, plain, says what to do next). */
export const COPY = {
  inviteOnly: 'Tahi is invite-only for now. Open the invite link you were sent.',
  invalidInvite:
    "This invite link isn't valid or has expired. Ask the person who invited you for a new one.",
  noAccount:
    "There's no Tahi account for that Google account. Tahi is invite-only for now, so open the invite link you were sent.",
  emailNotVerified:
    "Google hasn't verified that email address yet. Verify it with Google, or sign in with your email and password.",
  invalidEmail: 'Enter a valid email address.',
  badCredentials:
    "That email and password don't match. Try again, or ask your admin for a reset link.",
  locked: 'Too many tries. Wait 15 minutes, then try again.',
  exists: "There's already an account with this email. Sign in instead.",
  weak: 'Use at least 10 characters, and avoid common passwords.',
  tooLong: 'Use at most 128 characters.',
  mismatch: "The passwords don't match.",
  wrongCurrent: "Your current password doesn't match.",
  offline: "You're offline. Connect to the internet to sign in.",
  offlineTryAgain: "You're offline. Connect to the internet and try again.",
  googleFailed: "Google sign-in didn't finish. Try again.",
  resetExpired: 'This link has expired or was already used. Ask your admin for a new one.',
  resetDone: 'Password changed, and every device was signed out. Sign in with your new password.',
  passwordChanged: 'Password changed. Your other devices were signed out.',
  generic: 'Something went wrong. Try again in a moment.',
} as const;

/** "Too many tries" with the wait from Retry-After, rounded up to whole minutes. */
export function lockedCopy(retryAfter: number | undefined): string {
  if (!retryAfter || retryAfter <= 0) return COPY.locked;
  const minutes = Math.ceil(retryAfter / 60);
  return `Too many tries. Wait ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}, then try again.`;
}

export function retryAfterSeconds(res: Response | undefined): number | undefined {
  const raw = res?.headers.get('Retry-After');
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function describeAuthError(
  err: AuthErrorLike | null | undefined,
  ctx: AuthContext,
): string | null {
  if (!err) return null;
  const code = (err.code ?? '').toUpperCase();
  // The Worker answers every refused invite with the same code and never says why.
  if (code === 'INVALID_INVITE') return ctx === 'sign-in' ? COPY.noAccount : COPY.invalidInvite;
  if (code === 'EMAIL_NOT_VERIFIED') return COPY.emailNotVerified;
  if (err.status === 429 || code.startsWith('TOO_MANY')) return lockedCopy(err.retryAfter);
  if (code === 'INVALID_EMAIL') return COPY.invalidEmail;
  if (code.includes('ALREADY_EXISTS')) return COPY.exists;
  if (code === 'WEAK_PASSWORD') return err.message || COPY.weak;
  if (code === 'PASSWORD_TOO_LONG') return COPY.tooLong;
  if (code === 'PASSWORD_TOO_SHORT') return COPY.weak;
  if (ctx === 'change-password' && code === 'INVALID_PASSWORD') return COPY.wrongCurrent;
  if (ctx === 'sign-in' && (code === 'INVALID_EMAIL_OR_PASSWORD' || err.status === 401))
    return COPY.badCredentials;
  if (ctx === 'sign-up' && err.status === 403) return COPY.invalidInvite;
  return COPY.generic;
}

/**
 * The `?error=` code Better Auth adds when Google sign-in comes back refused: the Worker's own
 * codes (INVALID_INVITE, EMAIL_NOT_VERIFIED) or an OAuth one (access_denied, state_not_found, ...).
 */
export function describeCallbackError(code: string | null, ctx: AuthContext): string | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  if (upper === 'INVALID_INVITE' || upper === 'EMAIL_NOT_VERIFIED')
    return describeAuthError({ code: upper }, ctx);
  return COPY.googleFailed;
}

export function validatePassword(password: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) return COPY.weak;
  if (password.length > MAX_PASSWORD_LENGTH) return COPY.tooLong;
  return null;
}
